import { useMutation, useQuery } from '@tanstack/react-query';
import axiosInstance from '../axios';
import {
	PlaceOfSupplyDropDownResponse,
	PlaceOfSupplyPayload,
	PlaceOfSupplyResponse,
} from '../../type/PlaceOfSupply';
import { clearPageCache, getPageCache, putPageCache } from '../../utils/cache';

// Create Place Of Supply
const createPlaceOfSupply = async (
	payload: PlaceOfSupplyPayload,
): Promise<PlaceOfSupplyResponse> => {
	const response: any = await axiosInstance.post<PlaceOfSupplyResponse>(
		'/supply/createSupply',
		payload,
	);
	clearPageCache('place_of_supply_list');
	return response.data;
};

export const useCreatePlaceOfSupply = () => {
	return useMutation<PlaceOfSupplyResponse, Error, PlaceOfSupplyPayload>({
		mutationKey: ['createSupply'],
		mutationFn: createPlaceOfSupply,
	});
};

// Update Place Of Supply By ID
const updatePlaceOfSupplyById = async (
	payload: PlaceOfSupplyPayload,
): Promise<PlaceOfSupplyResponse> => {
	const response: any = await axiosInstance.post<PlaceOfSupplyResponse>(
		'/supply/updateSupplyById',
		payload,
	);
	clearPageCache('place_of_supply_list');
	return response.data;
};

export const useUpdatePlaceOfSupplyById = () => {
	return useMutation<PlaceOfSupplyResponse, Error, PlaceOfSupplyPayload>({
		mutationKey: ['updateSupplyById'],
		mutationFn: updatePlaceOfSupplyById,
	});
};

// List Supply
const listPlaceOfSupply = async (payload: PlaceOfSupplyPayload): Promise<PlaceOfSupplyResponse> => {
	if (getPageCache('place_of_supply_list', payload)) {
		return getPageCache('place_of_supply_list', payload);
	}
	const response: any = await axiosInstance.post<PlaceOfSupplyResponse>(
		'/supply/listSupplies',
		payload,
	);
	putPageCache('place_of_supply_list', payload, response.data.data);
	return response.data.data;
};

export const useListPlaceOfSupply = (payload: PlaceOfSupplyPayload) => {
	return useQuery<PlaceOfSupplyResponse, Error>({
		queryKey: ['listPlaceOfSupply', payload],
		queryFn: () => listPlaceOfSupply(payload),
		enabled: !!payload, // Optional: disables query until payload is available
	});
};

// Search Supply
const searchPlaceOfSupply = async (
	payload: PlaceOfSupplyPayload,
): Promise<PlaceOfSupplyResponse> => {
	const response: any = await axiosInstance.post<PlaceOfSupplyResponse>(
		'/supply/searchSupplies',
		payload,
	);
	return response.data.data;
};

export const useSearchPlaceOfSupply = () => {
	return useMutation<PlaceOfSupplyResponse, Error, PlaceOfSupplyPayload>({
		mutationKey: ['searchSupplies'],
		mutationFn: searchPlaceOfSupply,
	});
};

// Active Supply By ID
const activeSupplyById = async (payload: PlaceOfSupplyPayload): Promise<PlaceOfSupplyResponse> => {
	const response: any = await axiosInstance.post<PlaceOfSupplyResponse>(
		'/supply/activeSupplyById',
		payload,
	);
	clearPageCache('place_of_supply_list');
	return response.data;
};

export const useSupplyActiveById = () => {
	return useMutation<PlaceOfSupplyResponse, Error, PlaceOfSupplyPayload>({
		mutationKey: ['activeSupplyById'],
		mutationFn: activeSupplyById,
	});
};

// Deactive Supply By ID
const deActiveSupplyById = async (
	payload: PlaceOfSupplyPayload,
): Promise<PlaceOfSupplyResponse> => {
	const response: any = await axiosInstance.post<PlaceOfSupplyResponse>(
		'/supply/deactiveSupplyById',
		payload,
	);
	clearPageCache('place_of_supply_list');
	return response.data;
};

export const useSupplyDeActiveSupplyById = () => {
	return useMutation<PlaceOfSupplyResponse, Error, PlaceOfSupplyPayload>({
		mutationKey: ['deactiveSupplyById'],
		mutationFn: deActiveSupplyById,
	});
};

// Get Supply By ID
const getSupplyById = async (payload: PlaceOfSupplyPayload): Promise<PlaceOfSupplyResponse> => {
	if (getPageCache('place_of_supply_list', payload)) {
		return getPageCache('place_of_supply_list', payload);
	}
	const response: any = await axiosInstance.post<PlaceOfSupplyResponse>(
		'/supply/getSupplyById',
		payload,
	);
	putPageCache('place_of_supply_list', payload, response.data.data);
	return response.data.data;
};

export const useGetSupplyById = () => {
	return useMutation<PlaceOfSupplyResponse, Error, PlaceOfSupplyPayload>({
		mutationKey: ['getSupplyById'],
		mutationFn: getSupplyById,
	});
};

// Dropdown PlaceOfSupplier
const dropdownListPlaceOfSupplier = async (
	payload: PlaceOfSupplyPayload,
): Promise<PlaceOfSupplyDropDownResponse> => {
	const response = await axiosInstance.post('/util/queryPlaceOfSupplier', payload);
	return response.data;
};

export const useDropdownListPlaceOfSupplier = () => {
	return useMutation<PlaceOfSupplyDropDownResponse, Error, PlaceOfSupplyPayload>({
		mutationKey: ['queryPlaceOfSupplier'],
		mutationFn: dropdownListPlaceOfSupplier,
	});
};
